import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LOGIN_URL } from '@/lib/api';
import { cn } from '@/lib/utils';
import { FORMATS, VERTICALS, FEATURES } from '@/data/site-content';
import { LEAD_GEN_VERTICALS } from '@/data/lead-gen-content';
import BrandLogo from '@/components/branding/BrandLogo';

const SIMPLE_NAV = [
  { to: '/contact', label: 'Contact' },
];

const RESOURCES_ITEMS = [
  { to: '/blog', label: 'Blog', sub: 'Performance marketing playbooks, format guides, and scaling tactics.' },
  { to: '/case-studies', label: 'Case Studies', sub: 'Real-world lead-gen campaigns across real estate, credit cards, and insurance.' },
  { to: '/faq', label: 'FAQ', sub: 'Common advertiser questions about application, top-up, and traffic formats.' },
  { to: '/about', label: 'About', sub: 'Who runs Ads Dashbound and how we work with advertisers.' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header
      className="sticky top-0 z-50 bg-[#050B17]/75 backdrop-blur-2xl border-b border-[#22D3EE]/10"
      data-testid="site-header"
    >
      <div
        className="absolute inset-x-0 -bottom-px h-px pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.35), rgba(124,58,237,0.35), transparent)' }}
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="header-logo-link">
          <BrandLogo variant="full" size={36} />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" aria-label="Primary">
          <DropdownNav label="Ad Formats" parentPath="/ad-formats" testIdRoot="nav-formats" items={FORMATS.map((f) => ({ to: `/ad-formats/${f.slug}`, label: f.title, sub: f.short }))} viewAll={{ to: '/ad-formats', label: 'View all formats' }} />
          <DropdownNav label="Verticals" parentPath="/verticals" testIdRoot="nav-verticals" items={VERTICALS.map((v) => ({ to: `/verticals/${v.slug}`, label: v.title, sub: v.intro.slice(0, 64) + '…' }))} viewAll={{ to: '/verticals', label: 'All verticals' }} />
          <DropdownNav label="Features" parentPath="/features" testIdRoot="nav-features" items={FEATURES.map((f) => ({ to: `/features/${f.slug}`, label: f.title, sub: f.short }))} viewAll={{ to: '/features', label: 'All features' }} />
          <DropdownNav label="Lead Generation" parentPath="/lead-generation" testIdRoot="nav-leadgen" items={LEAD_GEN_VERTICALS.map((v) => ({ to: `/lead-generation/${v.slug}`, label: v.title, sub: v.short.slice(0, 90) + (v.short.length > 90 ? '…' : '') }))} viewAll={{ to: '/lead-generation', label: 'All lead-gen verticals' }} />
          <DropdownNav label="Resources" parentPath="/blog" testIdRoot="nav-resources" items={RESOURCES_ITEMS} viewAll={{ to: '/blog', label: 'Browse the blog' }} />
          {SIMPLE_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => cn(
                'relative px-3 py-2 rounded-lg text-[13.5px] font-medium transition-colors nav-underline',
                isActive ? 'text-white' : 'text-slate-300 hover:text-white',
              )}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="header-login-link"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[13.5px] font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            Login <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <Button
            asChild
            data-testid="header-advertiser-signup-button"
            className="btn-primary-gradient text-white rounded-xl px-4 py-2 h-10 text-[14px] font-semibold border-0"
          >
            <Link to="/apply" className="inline-flex items-center gap-1.5">Advertiser Sign Up <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          </Button>
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Open menu"
                data-testid="header-mobile-menu-button"
                className="text-white hover:bg-white/10"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#050B17] text-white border-l border-white/10 w-[88%] sm:w-[400px]">
              <div className="flex flex-col h-full pt-2">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-display text-lg font-bold">Menu</span>
                  <Button size="icon" variant="ghost" onClick={() => setOpen(false)} className="text-white hover:bg-white/10" aria-label="Close menu">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <nav className="flex flex-col gap-1 overflow-y-auto" aria-label="Mobile primary">
                  <MobileLink to="/ad-formats" onClose={() => setOpen(false)} label="Ad Formats" />
                  <MobileLink to="/verticals" onClose={() => setOpen(false)} label="Verticals" />
                  <MobileLink to="/features" onClose={() => setOpen(false)} label="Features" />
                  <MobileLink to="/lead-generation" onClose={() => setOpen(false)} label="Lead Generation" />
                  <MobileLink to="/case-studies" onClose={() => setOpen(false)} label="Case Studies" />
                  <MobileLink to="/blog" onClose={() => setOpen(false)} label="Blog" />
                  <MobileLink to="/about" onClose={() => setOpen(false)} label="About" />
                  <MobileLink to="/faq" onClose={() => setOpen(false)} label="FAQ" />
                  {SIMPLE_NAV.map((item) => (
                    <MobileLink key={item.to} to={item.to} label={item.label} onClose={() => setOpen(false)} />
                  ))}
                </nav>
                <div className="mt-auto flex flex-col gap-3 pb-2">
                  <a
                    href={LOGIN_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center justify-center gap-1 px-4 py-3 rounded-xl text-sm font-medium text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    Login <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                  <Button asChild className="btn-primary-gradient text-white rounded-xl py-6 border-0">
                    <Link to="/apply" onClick={() => setOpen(false)}>Advertiser Sign Up</Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function DropdownNav({ label, parentPath, items, viewAll, testIdRoot }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdRoot}-dropdown-trigger`}
          className="relative inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[13.5px] font-medium text-slate-300 hover:text-white nav-underline transition-colors"
          aria-expanded={open}
        >
          {label}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={10}
        align="start"
        className="w-[460px] p-2 rounded-2xl bg-[#0B172A]/95 border border-white/10 backdrop-blur-2xl text-white shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
      >
        <div className="grid grid-cols-2 gap-1">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className="group rounded-xl p-3 hover:bg-white/[0.05] transition-colors"
              data-testid={`${testIdRoot}-item-${it.to.split('/').pop()}`}
            >
              <div className="text-[13.5px] font-semibold text-white group-hover:text-[#22D3EE] transition-colors">{it.label}</div>
              <div className="mt-1 text-[12px] text-slate-400 leading-snug line-clamp-2">{it.sub}</div>
            </Link>
          ))}
        </div>
        <Link
          to={viewAll.to}
          onClick={() => setOpen(false)}
          className="mt-2 inline-flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-[13px] font-semibold text-[#22D3EE] hover:bg-white/[0.05] transition-colors border-t border-white/10"
        >
          {viewAll.label} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </PopoverContent>
    </Popover>
  );
}

function MobileLink({ to, label, onClose }) {
  return (
    <NavLink
      to={to}
      onClick={onClose}
      className={({ isActive }) => cn(
        'px-3 py-3 rounded-lg text-base font-medium transition-colors',
        isActive ? 'text-white bg-white/10' : 'text-slate-300 hover:text-white hover:bg-white/5',
      )}
      data-testid={`mobile-nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {label}
    </NavLink>
  );
}
